// src/features/fb/cookies/questions/CookiesQuestions.jsx
import React, { useState } from "react";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import {
  ProductNameQuestion,
  MarketPositioningQuestion,
  SKUWeightQuestion,
  AnnualVolumeQuestion,
} from "../../../../shared/modules/questions";

import ManufacturingCountryQuestion from "./CookiesManufacturingCountryQuestion";
import AnnualVolumeTextQuestion from "./AnnualVolumeTextQuestion";
import Standard from "./Standard";
import { isValidFbProduct, finiteDecimal, fbSkuUnitError, fbCountryError, usesSupportedFbCountries } from "../fbCostingDraft";

// Commodity-specific placeholder text mapping
const COMMODITY_PLACEHOLDERS = {
  Cookies: "e.g., Oats Cookie / Chocolate Chip Cookie / Butter Cookie / Cream Biscuit",
  "ICE CREAMS": "e.g., Vanilla Ice Cream / Chocolate Fudge / Strawberry Gelato / Mango Sorbet",
  "Ice Creams": "e.g., Vanilla Ice Cream / Chocolate Fudge / Strawberry Gelato / Mango Sorbet",
  "Carbonated Drinks": "e.g., Cola / Lemon-Lime Soda / Orange Fizz / Sparkling Water",
  Meat: "e.g., Chicken Sausage / Beef Patty / Turkey Franks / Pork Salami",
  carbonatedDrinks: "e.g., Cola / Lemon-Lime Soda / Orange Fizz / Sparkling Water",

  Shampoo: "e.g., Anti-Dandruff Shampoo / Moisturizing Shampoo / Volumizing Shampoo",
  Perfume: "e.g., Floral Perfume / Woody Perfume / Citrus Perfume",
};

const ICE_CREAM_DEMO_DEFAULTS = {
  productName: "Vanilla Ice Cream",
  marketPositioning: "Premium",
  skuWeight: "500",
  skuWeightUnit: "ml",
  annualVolume: "High (> 500,000 units)",
  manufacturingCountry: "India",
};

const DEMO_DEFAULTS_BY_COMMODITY = {
  cookies: {
    productName: "Oats Cookie",
    marketPositioning: "Standard",
    skuWeight: "200",
    skuWeightUnit: "g",
    annualVolume: "Medium (100,000 - 500,000 units)",
    manufacturingCountry: "India",
  },
  icecream: ICE_CREAM_DEMO_DEFAULTS,
  icecreams: ICE_CREAM_DEMO_DEFAULTS,
  carbonateddrinks: {
    productName: "Cola Soda",
    marketPositioning: "Standard",
    skuWeight: "330",
    skuWeightUnit: "ml",
    annualVolume: "High (> 500,000 units)",
    manufacturingCountry: "India",
  },
  meat: {
    productName: "Chicken Sausage",
    marketPositioning: "Standard",
    skuWeight: "500",
    skuWeightUnit: "g",
    annualVolume: "Medium (100,000 - 500,000 units)",
    manufacturingCountry: "India",
  },
  shampoo: {
    productName: "Anti-Dandruff Shampoo",
    marketPositioning: "Standard",
    skuWeight: "250",
    skuWeightUnit: "ml",
    annualVolume: "Medium (100,000 - 500,000 units)",
    manufacturingCountry: "India",
  },
  faceserum: {
    productName: "Vitamin C Face Serum",
    marketPositioning: "Premium",
    skuWeight: "30",
    skuWeightUnit: "ml",
    annualVolume: "Low (< 100,000 units)",
    manufacturingCountry: "India",
  },
  perfume: {
    productName: "Woody Perfume",
    marketPositioning: "Premium",
    skuWeight: "50",
    skuWeightUnit: "ml",
    annualVolume: "Low (< 100,000 units)",
    manufacturingCountry: "India",
  },
  bodylotion: {
    productName: "Aloe Body Lotion",
    marketPositioning: "Standard",
    skuWeight: "200",
    skuWeightUnit: "ml",
    annualVolume: "Medium (100,000 - 500,000 units)",
    manufacturingCountry: "India",
  },
  barsoap: {
    productName: "Charcoal Bar Soap",
    marketPositioning: "Standard",
    skuWeight: "100",
    skuWeightUnit: "g",
    annualVolume: "High (> 500,000 units)",
    manufacturingCountry: "India",
  },
};

const normalizeCommodityKey = (value) =>
  (value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "")
    .trim();

const getDemoDefaults = (commodity) =>
  DEMO_DEFAULTS_BY_COMMODITY[normalizeCommodityKey(commodity)] ||
  DEMO_DEFAULTS_BY_COMMODITY.cookies;

/**
 * CookiesQuestionsSection Component
 *
 * Encapsulates all product-related questions for F&B commodities
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {object} initialData - Initial data to populate the form
 * @param {string} commodity - Current commodity type (e.g., "Cookies", "ICE CREAMS")
 */
export default function CookiesQuestions({
  onComplete,
  onDataChange,
  onDraftChange,
  initialData,
  commodity = "Cookies",
}) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    },
  ); // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 4; // Last question
    if (initialData.annualVolume) return 3;
    if (initialData.skuWeight) return 2;
    if (initialData.marketPositioning) return 1;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());
  const [demoMode, setDemoMode] = useState(false);

  // Check if all questions are answered
  const isAllAnswered = isValidFbProduct(productFormData, commodity);

  // Notify parent when data changes and all questions are answered
  React.useEffect(() => {
    onDraftChange?.(productFormData);
    onDataChange?.(isAllAnswered ? productFormData : null);
  }, [productFormData, isAllAnswered, onDataChange, onDraftChange]);

  // Selected values for display
  const productSelectedValues = [
    { label: "Product Name:", value: productFormData.productName },
    { label: "Market Positioning:", value: productFormData.marketPositioning },
    {
      label: "SKU Weight:",
      value: productFormData.skuWeight
        ? `${productFormData.skuWeight} ${productFormData.skuWeightUnit}`
        : "",
    },
    { label: "Annual Volume:", value: productFormData.annualVolume },
    { label: "Manufacturing Country:", value: productFormData.manufacturingCountry },
  ];

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Market Positioning
        return !productFormData.marketPositioning;
      case 2: // SKU Weight
        return !(finiteDecimal(productFormData.skuWeight) > 0) || Boolean(fbSkuUnitError(productFormData, commodity));
      case 3: // Annual Volume
        return !productFormData.annualVolume;
      case 4: // Manufacturing Country
        return !productFormData.manufacturingCountry || Boolean(fbCountryError(productFormData.manufacturingCountry, commodity));
      default:
        return false;
    }
  };

  const handleComplete = () => {
    if (isAllAnswered) onComplete?.(productFormData);
  };

  const handleReset = () => {
    setProductFormData({
      productName: "",
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    });
    setProductChatStep(0);
    setDemoMode(false);
  };

  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (checked) {
      setProductFormData(getDemoDefaults(commodity));
      setProductChatStep(4);
    } else {
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={setProductChatStep}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      autoAdvanceOnBlur={false}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <ProductNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
        labelText={`Enter ${commodity} Product Name:`}
        placeHolderText={COMMODITY_PLACEHOLDERS[commodity] || `e.g., Premium ${commodity}`}
      />
      <MarketPositioningQuestion
        value={productFormData.marketPositioning}
        onChange={(value) => {
          setProductFormData({ ...productFormData, marketPositioning: value });
        }}
      />
      <SKUWeightQuestion
        value={productFormData.skuWeight}
        onChange={(value) => setProductFormData({ ...productFormData, skuWeight: value })}
        unit={productFormData.skuWeightUnit}
        unitOptions={["g", "kg", "ml", "L"]}
        onUnitChange={(value) => setProductFormData({ ...productFormData, skuWeightUnit: value })}
        error={fbSkuUnitError(productFormData, commodity)}
      />
      <AnnualVolumeQuestion
        value={productFormData.annualVolume}
        onChange={(value) => setProductFormData({ ...productFormData, annualVolume: value })}
      />
      {/* <AnnualVolumeTextQuestion
        value={productFormData.annualVolume}
        onChange={(value) => setProductFormData({ ...productFormData, annualVolume: value })}
        labelText="Enter Annual Volume"
      /> */}
      {/* <Standard
        value={productFormData.standard}
        onChange={(value) => setProductFormData({ ...productFormData, standard: value })}
        label="Select Standard"
      /> */}
      <ManufacturingCountryQuestion
        value={productFormData.manufacturingCountry}
        restrictToSupportedFbCountries={usesSupportedFbCountries(commodity)}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
